import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import DefaultLocales from 'globals/locales/default';
import { observer } from 'mobx-react';
import FsButton from 'ui-elements/buttons/FsButton';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import FsBodyMediumBold from 'ui-elements/typography/FsBodyMediumBold';
import FsSubtitle from 'ui-elements/typography/FsSubtitle';
import { getId } from 'globals/utils';
import reportLocales from 'globals/locales/report';
import Routes from 'routes';
import FsDialogTitle from 'ui-elements/modals/FsDialogTitle/FsDialogTitle';
import { ModalState } from 'globals/types';
import {
  CustomDialog,
  DialogActions,
  DialogContent,
  RelativeSendButtonWrapperBox,
} from '../ReportModals.style-elements';
import { ReportExportModalLocationStateProps } from '../ReportExportModal/ReportExportModal';

const { close: closeLabel } = DefaultLocales;

const {
  dialogTitle,
  alertTitle,
  alertDescription,
  subtitle,
} = reportLocales.exportModalSuccess;

const ReportExportModalSuccess: React.FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const modalState = location.state as ReportExportModalLocationStateProps;
  const status = modalState?.success;
  const fileList = modalState?.fileList || [];

  const redirect = useCallback((): void => {
    history.push({ pathname: Routes.REPORT });
  }, [history]);

  const handleClose = (): void => {
    redirect();
  };

  useEffect(() => {
    if (status !== ModalState.SUCCESS) redirect();
  }, [status, redirect]);

  return (
    <CustomDialog
      disableEnforceFocus
      open
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      data-test="report-export-modal-success"
    >
      <FsDialogTitle id="customized-dialog-title" onClose={handleClose}>
        {dialogTitle}
      </FsDialogTitle>

      <DialogContent dividers>
        <Alert severity="success">
          <AlertTitle>
            <FsBodyBold>{alertTitle}</FsBodyBold>
          </AlertTitle>
          <FsBody>
            {`${alertDescription[0]} `}
            <FsBodyMediumBold component="span">
              {alertDescription[1]}
            </FsBodyMediumBold>
            {` ${alertDescription[2]}`}
          </FsBody>
        </Alert>

        <Box marginTop={2}>
          <FsSubtitle>{subtitle}:</FsSubtitle>
        </Box>
        <List component="nav">
          {fileList.map(({ name, url }) => (
            <ListItem
              key={getId()}
              button
              component="a"
              download={name}
              href={url}
            >
              <ListItemIcon>
                <InsertDriveFileOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary={name} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <RelativeSendButtonWrapperBox>
          <FsButton onClick={handleClose} variant="contained" color="primary">
            {closeLabel}
          </FsButton>
        </RelativeSendButtonWrapperBox>
      </DialogActions>
    </CustomDialog>
  );
};

export default observer(ReportExportModalSuccess);
